import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Palette, TreePine, Mountain, Hammer, Flame, Leaf } from 'lucide-react';
import { SacredSection, ChristianIcon } from '../components/ChristianElements';

const Craft: React.FC = () => {
  const { t } = useTranslation();

  const processSteps = [
    {
      icon: Palette,
      title: t('craft.process.design.title'),
      description: t('craft.process.design.description'),
      step: 1
    },
    {
      icon: Hammer,
      title: t('craft.process.craft.title'),
      description: t('craft.process.craft.description'),
      step: 2
    },
    {
      icon: Mountain,
      title: t('craft.process.soil.title'),
      description: t('craft.process.soil.description'),
      step: 3
    },
    {
      icon: Leaf,
      title: t('materials.oliveBranch.title'),
      description: t('materials.oliveBranch.description'),
      step: 4
    },
    {
      icon: Flame,
      title: t('materials.incense.title'),
      description: t('materials.incense.description'),
      step: 5
    },
    {
      icon: TreePine,
      title: t('materials.cross.title'),
      description: t('materials.cross.description'),
      step: 6
    },
    {
      icon: Mountain,
      title: t('materials.rock.title'),
      description: t('materials.rock.description'),
      step: 7
    },
    
  ];

  return (
    <>
      <Helmet>
        <title>{t('meta.craft.title')}</title>
        <meta 
          name="description" 
          content={t('meta.craft.description')} 
        />
        <link rel="canonical" href={t('meta.craft.canonical')} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-12 md:pb-16 bg-primary-50 holy-land-bg">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center px-4">
            <div className="mb-4 md:mb-6">
              <ChristianIcon icon="vine" size="lg" className="mx-auto text-gold-600" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-scripture font-bold text-primary-800 mb-4 md:mb-6">
              {t('craft.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 px-2">
              {t('craft.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <SacredSection className="section-padding bg-white" title={t('craft.sections.sacredCraftingProcess')}>
        <div className="container-max">
          <div className="max-w-4xl mx-auto px-4">
            <div className="space-y-8 md:space-y-12">
              {processSteps.map((step, index) => (
                <div key={index} className="flex flex-col md:flex-row items-center gap-6 md:gap-8 sacred-border p-4 md:p-6 bg-primary-50/30">
                  <div className="flex-shrink-0 relative">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-primary-100 rounded-full flex items-center justify-center">
                      <step.icon className="w-8 h-8 md:w-10 md:h-10 text-primary-600" />
                    </div>
                    <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                      <ChristianIcon icon="cross" size="sm" />
                    </div>
                  </div>
                  
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-3 md:gap-4 mb-3 md:mb-4">
                      <span className="text-lg md:text-2xl font-bold text-primary-600">0{step.step}</span>
                      <h3 className="text-lg md:text-2xl font-scripture font-semibold text-primary-800">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-sm md:text-lg text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Scripture quote */}
            {/* <div className="mt-8 md:mt-12">
              <ScriptureQuote 
                verse={t('scriptures.hope.verse')}
                reference={t('scriptures.hope.reference')}
                className="max-w-2xl mx-auto"
              />
            </div> */}
          </div>
        </div>
      </SacredSection>

      {/* Tools & Techniques */}
      <section className="section-padding bg-primary-50">
        <div className="container-max">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-primary-800 mb-2 md:mb-4">
              {t('craft.toolsTechniques.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              {t('craft.toolsTechniques.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {(t('craft.toolsTechniques.techniques', { returnObjects: true }) as Array<{title: string, description: string}>).map((technique, index) => (
              <div key={index} className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg md:text-xl font-serif font-semibold text-primary-800 mb-2 md:mb-3">
                  {technique.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  {technique.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-primary-800 mb-4 md:mb-6">
                {t('craft.quality.title')}
              </h2>
              <div className="space-y-3 md:space-y-4 text-sm md:text-base text-gray-600">
                {(t('craft.quality.paragraphs', { returnObjects: true }) as string[]).map((paragraph, index) => (
                  <p key={index}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            
            <div className="bg-primary-50 rounded-lg overflow-hidden shadow-lg mt-6 lg:mt-0">
              <img 
                src="/images/finalcoffin.jpg" 
                alt="Quality crafted wooden coffin"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Craft;
